import React from "react";
import styles from "./IntegrationLogsModal.module.css";
import Modal from "../../components/layout/Modal";
import HBox from "../../components/layout/HBox";
import VBox from "../../components/layout/VBox";
import Titulo from "../../components/tipografia/Titulo";
import DataTable, { DataTableHeader, DataTableRow } from "../../components/layout/DataTable";
import CampoTexto from "../../components/Formulario/CampoTexto";
import CampoSeletor, { CampoSeletorOption } from "../../components/Formulario/CampoSeletor";
import Badge from "../../components/layout/Badge";
import { mdiMagnify, mdiLoading, mdiContentCopy, mdiCheck } from "@mdi/js";
import Icon from "../../components/Icon";
import { useInfiniteQuery, useQuery } from "react-query";
import axios from "axios";
import { useDebounce } from "../chat/useDebounce";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import Botao from "../../components/Botao";
import Grid, { GridItem } from "@/components/layout/Grid";

type IntegrationLogsModalProps = {
    isOpen: boolean;
    onClose: () => void;
    integrationId?: number | null;
};

type ResponseList = {
    status: number;
    ok?: boolean;
    result: any[];
    total: number;
    cursor: string | number | null;
};

export const IntegrationLogsModal: React.FC<IntegrationLogsModalProps> = ({
    isOpen,
    onClose,
    integrationId,
}) => {
    const [search, setSearch] = React.useState<string>("");
    const [integrationFilter, setIntegrationFilter] = React.useState<number | null>(integrationId ?? null);
    const [actionFilter, setActionFilter] = React.useState<string | null>(null);
    const [selectedMetadata, setSelectedMetadata] = React.useState<any>(null);
    const [showDetailsModal, setShowDetailsModal] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const debouncedSearch = useDebounce(search, 500);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    // Ao abrir o modal, marca o filtro de integração conforme a integração escolhida (se houver)
    React.useEffect(() => {
        if (isOpen) {
            setIntegrationFilter(integrationId ?? null);
            setActionFilter(null);
        }
    }, [isOpen, integrationId]);

    // Consulta a lista de integrações para montar as opções dos filtros
    const queryIntegrations = useQuery(
        ["integrations_box"],
        async () => {
            const response = await axios.get("/api/integrations/box", { withCredentials: true });
            return response.data;
        },
        {
            enabled: isOpen,
            staleTime: 1000 * 60 * 5,
        }
    );

    const integrationOptions: CampoSeletorOption[] = React.useMemo(
        () =>
            (queryIntegrations.data?.result ?? []).map((it: any) => ({
                id: it.id.toString(),
                label: it.name,
            })),
        [queryIntegrations.data]
    );

    // Opções de método/ação: restritas à integração selecionada, ou todas quando nenhuma está selecionada
    const actionOptions: CampoSeletorOption[] = React.useMemo(() => {
        const integrations = queryIntegrations.data?.result ?? [];
        const relevant = integrationFilter
            ? integrations.filter((it: any) => it.id === integrationFilter)
            : integrations;

        const names = new Set<string>();
        relevant.forEach((it: any) => (it.actions ?? []).forEach((a: any) => a?.name && names.add(a.name)));

        return Array.from(names)
            .sort()
            .map((name) => ({ id: name, label: name }));
    }, [queryIntegrations.data, integrationFilter]);

    // Limpa o filtro de método se ele deixar de ser válido para a integração selecionada
    React.useEffect(() => {
        if (actionFilter && !actionOptions.some((o) => o.id === actionFilter)) {
            setActionFilter(null);
        }
    }, [actionOptions]);

    // Consulta logs com query infinita
    const queryLogs = useInfiniteQuery<ResponseList>(
        ["integration_logs", integrationFilter, actionFilter, debouncedSearch],
        async ({ pageParam = 0 }) => {
            const params = new URLSearchParams({
                c: pageParam.toString(),
                search: debouncedSearch,
                ...(integrationFilter ? { integrationId: integrationFilter.toString() } : {}),
                ...(actionFilter ? { action: actionFilter } : {}),
            });

            const response = await axios.get<ResponseList>(
                `/api/integrations/logs?${params.toString()}`,
                { withCredentials: true }
            );
            return response.data;
        },
        {
            getNextPageParam: (lastPage) => lastPage.cursor,
            enabled: isOpen, // Só faz a consulta quando o modal está aberto
        }
    );

    // Implementa scroll infinito no container do modal
    React.useEffect(() => {
        const handleScroll = (e: Event) => {
            const target = e.target as HTMLElement;
            const scrollTop = target.scrollTop;
            const scrollHeight = target.scrollHeight;
            const clientHeight = target.clientHeight;

            // Carrega mais quando está a 200px do final
            if (
                scrollHeight - scrollTop - clientHeight < 200 &&
                queryLogs.hasNextPage &&
                !queryLogs.isFetchingNextPage
            ) {
                queryLogs.fetchNextPage();
            }
        };

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => {
                scrollContainer.removeEventListener('scroll', handleScroll);
            };
        }
    }, [queryLogs]);

    const handleCopyDetails = () => {
        if (selectedMetadata) {
            navigator.clipboard.writeText(JSON.stringify(selectedMetadata, null, 2))
                .then(() => {
                    setCopied(true);
                    setTimeout(() => {
                        setCopied(false);
                    }, 2000);
                })
                .catch(err => {
                    console.error("Erro ao copiar para a área de transferência:", err);
                });
        }
    };

    // Headers da tabela
    const cabecalhos: DataTableHeader[] = [
        {
            label: "Ação",
            column: "action"
        },
        {
            label: "Status",
            column: "status",
            render: (row) => (
                <Badge
                    variant={
                        row.status === "success"
                            ? "success"
                            : row.status === "error"
                                ? "danger"
                                : "warning"
                    }
                >
                    {row.status}
                </Badge>
            )
        },
        {
            label: "Data/Hora",
            column: "timestamp",
            render: (row) => (
                row.timestamp
                    ? format(new Date(row.timestamp), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })
                    : "-"
            )
        },
        {
            label: "Duração (ms)",
            column: "duration_ms"
        },
        {
            label: "Detalhes",
            column: "metadata",
            render: (row) => {
                const handleViewDetails = () => {
                    if (row.metadata) {
                        setSelectedMetadata(row.metadata);
                        setShowDetailsModal(true);
                        setCopied(false);
                    }
                };

                return row.metadata ? (
                    <div
                        className={styles.metadataContainer}
                        onClick={handleViewDetails}
                        title="Clique para ver os detalhes completos"
                        style={{ cursor: 'pointer' }}
                    >
                        <pre className={styles.metadataPreview}>
                            {JSON.stringify(row.metadata, null, 2).slice(0, 50)}
                            {JSON.stringify(row.metadata, null, 2).length > 50 ? "..." : ""}
                        </pre>
                    </div>
                ) : (
                    "-"
                );
            }
        },
    ];

    return (
        <>
            <Modal
                show={isOpen}
                onClose={onClose}
                title="Logs de Integração"
                size="lg"
                hideControls
            >
                <VBox gap={16} ref={scrollContainerRef} style={{ maxHeight: '65vh', overflow: 'auto' }}>
                    <Grid>
                        <GridItem sm={12} md={6}>
                            <div style={{ width: '100%', padding: '4px' }}>
                                <CampoSeletor
                                    placeholder="Todas as integrações"
                                    value={integrationFilter ? integrationFilter.toString() : null}
                                    onChange={(v) => setIntegrationFilter(v ? parseInt(v) : null)}
                                    options={integrationOptions}
                                    loadingState={queryIntegrations.isLoading ? "loading" : "idle"}
                                    nullable
                                />
                            </div>
                        </GridItem>
                        <GridItem sm={12} md={6}>
                            <div style={{ width: '100%', padding: '4px' }}>
                                <CampoSeletor
                                    placeholder="Todos os métodos"
                                    value={actionFilter}
                                    onChange={setActionFilter}
                                    options={actionOptions}
                                    loadingState={queryIntegrations.isLoading ? "loading" : "idle"}
                                    nullable
                                />
                            </div>
                        </GridItem>
                    </Grid>

                    <div className={styles.tableContainer}>
                        <DataTable
                            cabecalhos={cabecalhos}
                            linhas={
                                queryLogs.data?.pages
                                    .flatMap((page) => page.result) || []
                            }
                            isLoading={queryLogs.isLoading}
                        />
                    </div>

                    {queryLogs.isFetchingNextPage && (
                        <div style={{ textAlign: 'center', padding: '16px' }}>
                            <Icon path={mdiLoading} size={1} color="var(--tc-color-gray-700)" spin />
                            {' '}Carregando mais logs...
                        </div>
                    )}

                    {queryLogs.data?.pages.flatMap((page) => page.result).length === 0 && !queryLogs.isLoading && (
                        <div className={styles.emptyMessage}>
                            Nenhum log encontrado.
                        </div>
                    )}
                </VBox>
            </Modal>

            {/* Modal de Detalhes */}
            <Modal
                show={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                title="Detalhes da Integração"
                size="md"
                hideControls
            >
                <VBox gap={16}>
                    <HBox style={{ justifyContent: 'flex-end' }}>
                        <Botao
                            variant={copied ? "success" : "outline-primary"}
                            onClick={handleCopyDetails}
                            icon={copied ? mdiCheck : mdiContentCopy}
                        >
                            {copied ? "Copiado!" : "Copiar"}
                        </Botao>
                    </HBox>
                    <div className={styles.detailsContent}>
                        <pre className={styles.detailsJson}>
                            {selectedMetadata ? JSON.stringify(selectedMetadata, null, 2) : ""}
                        </pre>
                    </div>
                </VBox>
            </Modal>
        </>
    );
};
